import {
    areaValueForRow,
    type CoverageArea,
} from '@apps/web/features/coverage/model/area_model';
import {
    type CoverageRow,
    DAY_INDEXES,
    DAY_PARTS,
    type DayPartKey,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import { columnDayIndexes } from '@apps/web/features/coverage/model/rollup_model';

/** One grid cell, identified the way the grid renders it. */
export type CoverageSelectedCell = {
    readonly areaKey: string;
    readonly group: 'weekday' | 'weekend';
    readonly dayPart: DayPartKey;
};

/**
 * The clicked cells are the only truth. Areas, days and day parts are
 * projections of them, derived where a caller needs one, never stored
 * beside them: two peer encodings let a reader pick the wrong one.
 */
export type CoverageSelection =
    | { readonly kind: 'none' }
    | { readonly kind: 'area'; readonly areaKeys: ReadonlyArray<string> }
    | {
          readonly kind: 'cells';
          readonly cells: ReadonlyArray<CoverageSelectedCell>;
      };

export const EMPTY_SELECTION: CoverageSelection = { kind: 'none' };

export function isSelectionEmpty(selection: CoverageSelection): boolean {
    switch (selection.kind) {
        case 'none':
            return true;
        case 'area':
            return selection.areaKeys.length === 0;
        case 'cells':
            return selection.cells.length === 0;
        default:
            return true;
    }
}

export function selectedAreaKeys(
    selection: CoverageSelection,
): ReadonlyArray<string> {
    switch (selection.kind) {
        case 'none':
            return [];
        case 'area':
            return selection.areaKeys;
        case 'cells':
            return [...new Set(selection.cells.map((cell) => cell.areaKey))];
        default:
            return [];
    }
}

/** Day parts across the whole selection, for labels and message copy. */
export function selectedDayParts(
    selection: CoverageSelection,
): ReadonlyArray<DayPartKey> {
    if (selection.kind !== 'cells') {
        return [];
    }
    return [...new Set(selection.cells.map((cell) => cell.dayPart))];
}

/** One key scheme for a cell, shared by the marquee and the highlight. */
export function cellKey(
    areaKey: string,
    group: 'weekday' | 'weekend',
    dayPart: DayPartKey,
): string {
    return `${areaKey}|${group}|${dayPart}`;
}

/** Memoize per render: scanning the array per cell is quadratic. */
export function selectedCellKeys(
    selection: CoverageSelection,
): ReadonlySet<string> {
    if (selection.kind !== 'cells') {
        return new Set();
    }
    return new Set(
        selection.cells.map((cell) =>
            cellKey(cell.areaKey, cell.group, cell.dayPart),
        ),
    );
}

export function isCellSelected(
    selection: CoverageSelection,
    areaKey: string,
    group: 'weekday' | 'weekend',
    dayPart: DayPartKey,
    selectedKeys: ReadonlySet<string>,
): boolean {
    if (selection.kind === 'area') {
        return selection.areaKeys.includes(areaKey);
    }
    return selectedKeys.has(cellKey(areaKey, group, dayPart));
}

/**
 * Slots to judge one area's caregivers against. Scoped by area so a cell
 * clicked for Boulder can never classify a Longmont caregiver.
 */
export function slotsForArea(
    selection: CoverageSelection,
    areaKey: string,
    workWeekStartDay: WorkWeekStartDay,
): ReadonlyArray<readonly [number, DayPartKey]> {
    const wholeWeek = (): ReadonlyArray<readonly [number, DayPartKey]> =>
        DAY_INDEXES.flatMap((dayIndex) =>
            DAY_PARTS.map((part): readonly [number, DayPartKey] => [
                dayIndex,
                part.key,
            ]),
        );
    if (selection.kind === 'none') {
        return [];
    }
    // A whole-area ask names no cell, so it covers the whole week.
    if (selection.kind === 'area') {
        return selection.areaKeys.includes(areaKey) ? wholeWeek() : [];
    }
    return selection.cells
        .filter((cell) => cell.areaKey === areaKey)
        .flatMap((cell) =>
            columnDayIndexes(cell.group, workWeekStartDay).map(
                (dayIndex): readonly [number, DayPartKey] => [
                    dayIndex,
                    cell.dayPart,
                ],
            ),
        );
}

/** Everyone in the selected areas, whatever their answer for the slot. */
export function rowsForSelection(
    rows: ReadonlyArray<CoverageRow>,
    selection: CoverageSelection,
    area: CoverageArea,
): ReadonlyArray<CoverageRow> {
    const areaKeys = selectedAreaKeys(selection);
    if (areaKeys.length === 0) {
        return [];
    }
    return rows.filter((row) =>
        areaKeys.includes(areaValueForRow(row, area).key),
    );
}

function dayPartLabels(selection: CoverageSelection): ReadonlyArray<string> {
    return selectedDayParts(selection).map(
        (key) => DAY_PARTS.find((part) => part.key === key)?.label ?? key,
    );
}

/** The selection worded for mid-sentence, where a heading reads wrong. */
export function selectionSentenceFor(
    selection: CoverageSelection,
    areaLabelFor: (areaKey: string) => string,
): string | null {
    if (isSelectionEmpty(selection)) {
        return null;
    }
    const areaKeys = selectedAreaKeys(selection);
    // Reads inside an outbound SMS, so "3 areas shifts" is never allowed.
    const areaPart =
        areaKeys.length === 1 ? areaLabelFor(areaKeys[0] ?? '') : null;
    const slots = dayPartLabels(selection).map((label) => label.toLowerCase());
    if (slots.length === 0) {
        return areaPart;
    }
    const joined =
        slots.length === 1
            ? (slots[0] ?? '')
            : `${slots.slice(0, -1).join(', ')} and ${slots[slots.length - 1]}`;
    return areaPart === null ? joined : `${areaPart} ${joined}`;
}

export function selectionLabelFor(
    selection: CoverageSelection,
    areaLabelFor: (areaKey: string) => string,
): string | null {
    if (isSelectionEmpty(selection)) {
        return null;
    }
    const areaKeys = selectedAreaKeys(selection);
    const areaPart =
        areaKeys.length === 1
            ? areaLabelFor(areaKeys[0] ?? '')
            : `${areaKeys.length} areas`;
    const slots = dayPartLabels(selection);
    if (slots.length === 0) {
        return `${areaPart} · whole week`;
    }
    return `${areaPart} · ${slots.join(', ')}`;
}
